import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Geographies, Geography as GeographySimpleMap, useGeographies } from 'react-simple-maps';

/**
 * Function returning function for dynamically calculating fill/color or stroke
 *
 * @type {function}
 * @param {*} prop function or static value for fill or stroke
 * @returns {function}
 */
const asFunc = (prop) => {
  if (typeof prop === 'function') return prop;
  return () => prop;
};

/**
 * A function that returns `Geography` component for overlay
 *
 * @type {function}
 * @param {*} geography JSON data for overlay
 * @param {*} rest other props
 * @returns {Component}
 */
const GeoOverlay = ({ geography, ...rest }) => {
  const { geographies } = useGeographies({ geography });
  return geographies.map((geo) => <GeographySimpleMap key={geo.rsmKey} geography={geo} {...rest} />);
};

/**
 * **Access:**
 * ```js
 * import { Geography } from '@iss/client-react-components';
 * ```
 * A Geography component for the world map.
 * It renders different regions of the map.
 * We can either pass valid geojson or topojson object or geojson array directly to `json` props of this component.
 */
export default class Geography extends Component {
  static propTypes = {
    /** JSON data for a map with an array of coordinates and properties. */
    json: PropTypes.shape({}).isRequired,
    /** Consists of JSON data of shape (like polygon) that will be rendered on the top of the underlying map.*/
    overlays: PropTypes.arrayOf(PropTypes.shape({})),
    /** Add a border to regions of the map. Provide a function for dynamically define the border.*/
    stroke: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /** Fill colors to individual region of map. Provide a function for dynamically calculate the color for a different region. */
    fill: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /** Specify opacity/transparency of map. Provide a function to dynamically define opacity. */
    opacity: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
  };

  static defaultProps = {
    overlays: null,
    stroke: '#FFFFFF',
    fill: '#EEEEEE',
    opacity: 1,
  };

  render() {
    const { json, overlays, stroke, fill, opacity, ...mapProps } = this.props;

    return (
      <Geographies geography={json} {...mapProps}>
        {({ geographies }) => {
          return geographies
            .map((geo) => (
              <GeographySimpleMap
                key={geo.rsmKey}
                geography={geo}
                fill={asFunc(fill)(geo)}
                stroke={asFunc(stroke)(geo)}
              />
            ))
            .concat((overlays || []).map((props, index) => <GeoOverlay key={index} {...props} />));
        }}
      </Geographies>
    );
  }
}
